import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../api/client';
import { useAsync } from '../hooks/useAsync';
import { useSideNav } from '../context/SideNavContext';
import { useAuth } from '../context/AuthContext';
import PageHeader from '../components/PageHeader';
import Card from '../components/Card';
import StatCard from '../components/StatCard';
import Icon from '../components/Icon';
import { Loading, ErrorState, EmptyState } from '../components/States';
import { number, relativeTime } from '../lib/format';
import { ICONS } from '../lib/icons';
import { providerNav } from '../lib/providerNav';

// The provider's public price catalogue: the SKU part number → name/unit mapping
// the platform bills against, with the vendor's published list price beside it.
//
// The list prices here are Oracle's PUBLIC pay-as-you-go rates, kept as a
// reference for whoever sets a group's billing price. They are never what a
// client is charged and never what margin is measured against — that stays with
// what OCI actually billed on the usage row.

// List prices run to fractions of a cent (0.0015 per GB hour), so they need far
// more precision than the 2-decimal money format used for amounts.
const priceText = (v) =>
  (v ?? 0).toLocaleString('en-US', { maximumFractionDigits: 8 });

// Volume thresholds are large round numbers (950,000 requests), so they read
// better abbreviated than in full.
const volumeText = (v) => {
  if (v >= 1e9) return `${Number((v / 1e9).toFixed(2))}B`;
  if (v >= 1e6) return `${Number((v / 1e6).toFixed(2))}M`;
  if (v >= 1e3) return `${Number((v / 1e3).toFixed(2))}K`;
  return number(v);
};

// One band reads as a plain rate. Several read as a range each, since which one
// applies depends on how much is used — and the first is often a free allowance,
// so collapsing them to a single number would misprice the SKU.
const bandText = (band) => {
  const price = `${priceText(band.price)} ${band.currency}`;
  if (!band.rangeMin && !band.rangeMax) return price;
  const from = volumeText(band.rangeMin || 0);
  return band.rangeMax ? `${price} · ${from}–${volumeText(band.rangeMax)}` : `${price} · ${from}+`;
};

export default function ProviderPriceList() {
  const { id } = useParams();
  const { setNav } = useSideNav();
  const { isAdmin } = useAuth();

  const [query, setQuery] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [syncError, setSyncError] = useState(null);

  const providerReq = useAsync(() => api.getProvider(id), [id]);
  // The catalogue holds every currency the vendor publishes; one is read at a
  // time, since a rate in the wrong currency is worse than no rate at all.
  const skusReq = useAsync(() => api.getProviderSkus(id, currency), [id, currency]);

  const provider = providerReq.data;

  // Publish the provider submenu while this page is mounted.
  useEffect(() => {
    if (provider) setNav(providerNav(provider));
  }, [provider, setNav]);
  useEffect(() => () => setNav(null), [setNav]);

  const skus = useMemo(() => skusReq.data?.data || [], [skusReq.data]);
  const currencies = useMemo(() => skusReq.data?.meta?.currencies || [], [skusReq.data]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return skus;
    return skus.filter((s) =>
      `${s.skuId} ${s.skuName} ${s.serviceCategory} ${s.unit}`.toLowerCase().includes(q)
    );
  }, [skus, query]);

  // How fresh the catalogue is: every row is written by the same sync, so any
  // row's timestamp answers it.
  const lastSynced = skus[0]?.updatedAt;
  const categories = useMemo(
    () => new Set(skus.map((s) => s.serviceCategory).filter(Boolean)).size,
    [skus]
  );

  const sync = async () => {
    setSyncing(true);
    setSyncResult(null);
    setSyncError(null);
    try {
      const res = await api.syncProviderSkus(id);
      setSyncResult(res);
      skusReq.reload();
    } catch (err) {
      setSyncError(err.message);
    } finally {
      setSyncing(false);
    }
  };

  if (providerReq.loading) return <Loading label="Loading provider…" />;
  if (providerReq.error) {
    return <ErrorState error={providerReq.error} onRetry={providerReq.reload} />;
  }

  return (
    <div className="stack">
      <PageHeader
        title="Public price list"
        subtitle={`${provider?.name || id} — SKU catalogue and published pay-as-you-go rates`}
        actions={
          isAdmin ? (
            <button className="btn btn--primary" onClick={sync} disabled={syncing}>
              <Icon name={ICONS.refresh} /> {syncing ? 'Syncing…' : 'Sync from provider'}
            </button>
          ) : null
        }
      />

      <div className="grid grid--kpi">
        <StatCard
          label="SKUs"
          value={number(skus.length)}
          hint={lastSynced ? `synced ${relativeTime(lastSynced)}` : 'never synced'}
        />
        <StatCard label="Service categories" value={number(categories)} />
        <StatCard
          label="Showing"
          value={number(filtered.length)}
          hint={query ? 'matching your search' : 'all SKUs'}
        />
      </div>

      {syncError ? <div className="form-error">{syncError}</div> : null}
      {syncResult ? (
        <p className="field__hint">
          Synced <strong>{number(syncResult.skus)}</strong> SKUs and{' '}
          <strong>{number(syncResult.prices)}</strong> published rates across{' '}
          <strong>{number(syncResult.currencies)}</strong> currencies.
          {syncResult.lastUpdated
            ? ` Catalogue published ${new Date(syncResult.lastUpdated).toLocaleDateString()}.`
            : ''}{' '}
          {syncResult.added || syncResult.changed || syncResult.removed ? (
            <>
              Compared with the version before it: {number(syncResult.added)} band
              {syncResult.added === 1 ? '' : 's'} added, {number(syncResult.changed)} repriced,{' '}
              {number(syncResult.removed)} removed.{' '}
            </>
          ) : (
            'Nothing published differs from the previous sync. '
          )}
          <Link to={`/providers/${id}/price-list/history`}>View sync history</Link>
        </p>
      ) : null}

      <Card
        title={`SKUs ${skus.length ? `(${number(skus.length)})` : ''}`}
        action={
          <div className="usage-export">
            <input
              className="input input--sm"
              placeholder="Search part number, name, category…"
              aria-label="Search the price list"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <select
              className="input input--sm"
              aria-label="Price currency"
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
            >
              {(currencies.length ? currencies : [currency]).map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>
        }
      >
        {skusReq.loading ? (
          <Loading label="Loading price list…" />
        ) : skusReq.error ? (
          <ErrorState error={skusReq.error} onRetry={skusReq.reload} />
        ) : skus.length === 0 ? (
          <EmptyState
            title="No catalogue stored yet"
            detail={
              isAdmin
                ? 'Click “Sync from provider” to pull the published price list.'
                : 'Ask an administrator to sync the provider price list.'
            }
          />
        ) : filtered.length === 0 ? (
          <EmptyState title="No SKU matches that search" detail={`Nothing matches “${query}”.`} />
        ) : (
          <div className="table-wrap">
            <table className="table table--dense">
              <thead>
                <tr>
                  <th>Part number</th>
                  <th>Name</th>
                  <th>Service category</th>
                  <th>Unit</th>
                  <th className="num">List price</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((s) => (
                  <tr key={s.skuId}>
                    <td className="cell-type">{s.skuId}</td>
                    <td className="cell-primary">{s.skuName || '—'}</td>
                    <td>{s.serviceCategory || '—'}</td>
                    <td>{s.unit || '—'}</td>
                    <td className="num">
                      {(s.prices || []).length === 0 ? (
                        '—'
                      ) : (
                        <span title={`Public rate per ${s.unit || 'unit'}`}>
                          {s.prices.map((b, bi) => (
                            <div key={`${s.skuId}-${bi}`}>{bandText(b)}</div>
                          ))}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <p className="field__hint">
        These are the vendor's published pay-as-you-go rates, shown for reference
        while setting a group's billing price. What a client is charged comes from
        Price management, and margin is always measured against what OCI actually
        billed.
      </p>
    </div>
  );
}
